import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  defaultServerConfig,
  servers,
  ServerTypes,
} from '../../app/models/servers';
import { CoreStorageEnum } from 'src/app/models/core-storage.enum';
import { StorageService } from './storage.service';

@Injectable()
export class ApiService {
  constructor(private storage: StorageService) {}

  private currentServer = defaultServerConfig;

  public updateApi2(serverType: ServerTypes): boolean {
    this.setNextIndex(serverType);
    return true;
  }
  // Host fragments as they appear in the server URLs handed out at login,
  // used only when the url is not in the `servers` table (yet).
  private static readonly hostFragments: [string, ServerTypes][] = [
    ['bo-ptdesk', ServerTypes.ptdesk],
    ['bo-auth', ServerTypes.auth],
    ['bo-admin', ServerTypes.admin],
    ['bo-work', ServerTypes.work],
    ['bo-tfw', ServerTypes.tfw],
    ['bo-api', ServerTypes.boapi],
  ];

  // Returns null for a url we cannot place — callers must not fall back to a
  // guess, or a failing request gets retried against the wrong server pool.
  public getServerTypeByUrl(url: string): ServerTypes {
    if (!url) return null;

    const known = servers.find(
      (q) => !!q.url && q.url.some((z) => !!z && url.startsWith(z))
    );
    if (known) return known.type;

    const match = ApiService.hostFragments.find(([fragment]) => url.includes(fragment));
    return match ? match[1] : null;
  }

  public getCurentServer(serverType: ServerTypes) {
    const index = this.getCurrentIndex(serverType);
    return this.getServerByIndex(serverType, index);
  }

  // Server hosts arrive from two places -- login hands some over, the `servers`
  // table hardcodes the rest -- so some carry a trailing slash and some do not,
  // and resource urls are written both with and without a leading one. Join the
  // two on exactly one slash so we never emit `host///v1/...`.
  public joinUrl(serverType: ServerTypes, url: string): string {
    const host = String(this.getCurentServer(serverType)).replace(/\/+$/, '');
    return `${host}/${String(url ?? '').replace(/^\/+/, '')}`;
  }

  public  getReslienceHeaders(serverType: ServerTypes): HttpHeaders {
    return new HttpHeaders({
      'X-resilience-server': serverType,
      'X-resilience-retries':0,
    });
  }

  public getHeaders(serverType: ServerTypes, retries: number) {
    if (this.storage.getSessionStorageItem(CoreStorageEnum.testId)) {
      return new HttpHeaders({
        'X-testId': this.storage.getSessionStorageItem(CoreStorageEnum.testId),
        'X-resilience-server': serverType,
        'X-resilience-retries': retries.toString(),
      });
    }
    if (environment.testId != null) {
      return new HttpHeaders({
        'X-testId': environment.testId.toString(),
        'X-resilience-server': serverType,
        'X-resilience-retries': retries.toString(),
      });
    }
    return new HttpHeaders({
      'X-resilience-server': serverType,
      'X-resilience-retries': retries.toString(),
    });
  }

  public parseUrl(url: string, type: ServerTypes) {
    let server;
    servers.forEach((q) => {
      if (type === q.type) {
        q.url.forEach((z) => {
          if (url.startsWith(z)) {
            server = z;
          }
        });
      }
    });
    return server;
  }

  private getCurrentIndex(serverType: ServerTypes) {
    let index: number = 0;
    this.currentServer.forEach((q) => {
      if (q.type === serverType) {
        index = q.index;
      }
    });
    return index;
  }

  private setNextIndex(serverType: ServerTypes) {
    let index: number = this.getCurrentIndex(serverType) + 1;
    if (this.getServerByIndex(serverType, index) === null) {
      index = 0;
    }
    this.currentServer.forEach((q) => {
      if (q.type === serverType) {
        q.index = index;
      }
    });
  }

  private getServerByIndex(type: ServerTypes, index: number) {
    let server = null;
    servers.forEach((q) => {
      if (type === q.type && q.url.length - 1 >= index) {
        server = q.url[index];
      }
    });
    return server;
  }
}
